'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from 'lucide-react'
import { formatImageURL, getBorderColorStyles, getVendorPrimaryColor } from '@/utils/util'
import { Button } from '@/components/elements/Button'

interface ImageCarouselProps {
  images: string[]
  initialImage: string
  isOpen: boolean
  onClose: () => void
  productName: string
  vendor: any
}

export default function ImageCarousel({ images, initialImage, isOpen, onClose, productName, vendor }: ImageCarouselProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isZoomed, setIsZoomed] = useState(false)
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 })
  const [isMobile, setIsMobile] = useState(false)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const imageRef = useRef<HTMLImageElement>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const minSwipeDistance = 50

  const navigateNext = useCallback(() => {
    setIsZoomed(false)
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }, [images.length])

  const navigatePrev = useCallback(() => {
    setIsZoomed(false)
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }, [images.length])

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  useEffect(() => {
    if (isOpen && initialImage) {
      const index = images.findIndex(img => img === initialImage)
      setCurrentImageIndex(index >= 0 ? index : 0)
    }
    setIsZoomed(false)
  }, [isOpen, initialImage, images])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        navigatePrev()
      } else if (e.key === 'ArrowRight') {
        navigateNext()
      } else if (e.key === 'Escape') {
        if (isZoomed) {
          setIsZoomed(false)
        } else {
          onClose()
        }
      }
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (
        imageRef.current &&
        !imageRef.current.contains(e.target as Node) &&
        carouselRef.current &&
        carouselRef.current.contains(e.target as Node) &&
        !(e.target as HTMLElement).closest('button')
      ) {
        if (isZoomed) {
          setIsZoomed(false)
        } else {
          onClose()
        }
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, isZoomed, navigateNext, navigatePrev, onClose])

  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isOpen])

  const toggleZoom = (e: React.MouseEvent) => {
    if (isMobile) return
    if (!isZoomed) {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        const x = ((e.clientX - rect.left) / rect.width) * 100
        const y = ((e.clientY - rect.top) / rect.height) * 100
        setZoomPosition({ x, y })
        setIsZoomed(true)
      }
    } else {
      setIsZoomed(false)
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isMobile || !isZoomed) return
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const x = ((e.clientX - rect.left) / rect.width) * 100
      const y = ((e.clientY - rect.top) / rect.height) * 100
      setZoomPosition({ x, y })
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      navigateNext()
    } else if (isRightSwipe) {
      navigatePrev()
    }
    setTouchStart(0)
    setTouchEnd(0)
  }

  if (!isOpen) return null

  const primaryColor = getVendorPrimaryColor(vendor)
  const { className: borderColorClass, style: borderColorStyle } = getBorderColorStyles(primaryColor)

  return (
    <div ref={carouselRef} className="fixed inset-0 z-50 bg-black/75 transition-opacity flex flex-col items-center justify-center">
      <Button onClick={onClose} variant='link' size='xs' className="absolute top-4 right-4 text-zinc-100 hover:text-zinc-400 transiton-colors py-6 z-10" aria-label="Close image carousel">
        <X className="h-8 w-8" />
      </Button>

      {!isMobile && (
        <Button onClick={(e) => { e.stopPropagation(); setIsZoomed(!isZoomed) }} variant='link' size='xs' className="absolute top-4 left-4 text-zinc-100 hover:text-zinc-400 transition-colors py-6 z-10" aria-label={isZoomed ? "Zoom out" : "Zoom in"}>
          {isZoomed ? <ZoomOut className="h-6 w-6" /> : <ZoomIn className="h-6 w-6" />}
        </Button>
      )}

      <div ref={containerRef} onMouseMove={handleMouseMove} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} className="relative w-full h-full flex items-center justify-center p-4 md:p-10 overflow-hidden">
        <div className={`transition-transform duration-200 ${isZoomed ? 'scale-200' : 'scale-100'}`} style={{ transformOrigin: isZoomed ? `${zoomPosition.x}% ${zoomPosition.y}%` : 'center', cursor: isZoomed ? 'zoom-out' : 'zoom-in' }}>
          <img ref={imageRef} src={formatImageURL(images[currentImageIndex])} alt={`${productName} - image ${currentImageIndex + 1}`} className="max-h-[80vh] max-w-full object-contain rounded-lg" onClick={toggleZoom} />
        </div>

        {(!isZoomed && !isMobile) && (
          <>
            <button onClick={(e) => { e.stopPropagation(); navigatePrev() }} className="absolute left-4 bg-foreground/75 rounded-full p-2 text-zinc-100 hover:bg-foreground transition-opacity" aria-label="Previous image">
              <ChevronLeft className="h-8 w-8" />
            </button>

            <button onClick={(e) => { e.stopPropagation(); navigateNext() }} className="absolute right-4 bg-foreground/75 rounded-full p-2 text-zinc-100 hover:bg-foreground transition-opacity" aria-label="Next image">
              <ChevronRight className="h-8 w-8" />
            </button>
          </>
        )}
      </div>

      {(!isZoomed || isMobile) && (
        <div className="absolute bottom-4 left-0 right-0" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-center gap-2 px-4 overflow-x-auto pb-4">
            {images.map((image, index) => (
              <button key={index} onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); setIsZoomed(false) }} className={`w-16 h-16 border-2 rounded shrink-0 ${index === currentImageIndex ? `${borderColorClass}` : 'border-transparent'}`} style={index === currentImageIndex ? borderColorStyle : {}}>
                <img src={formatImageURL(image)} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover rounded" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}